export interface CapabilityReport {
  timestamp: string;
  userAgent: string;
  viewport: {
    width: number;
    height: number;
    devicePixelRatio: number;
    orientation: string;
  };
  features: {
    webCodecs: boolean;
    h264Baseline: boolean;
    h264Main: boolean;
    h264High: boolean;
    webAssembly: boolean;
    wasmSimd: boolean;
    webWorker: boolean;
    canvas2d: boolean;
    webgl: boolean;
    webgl2: boolean;
    mediaSource: boolean;
    webRtc: boolean;
    audioContext: boolean;
    touchEvents: boolean;
    pointerEvents: boolean;
    privateNetworkAccess: boolean;
  };
  selectedRenderer: 'webcodecs' | 'wasm-h264' | 'canvas-fallback';
}

/**
 * Execute capability probe to evaluate browser environment.
 */
export async function runCapabilityProbe(): Promise<CapabilityReport> {
  const userAgent = navigator.userAgent;
  const viewport = {
    width: window.innerWidth,
    height: window.innerHeight,
    devicePixelRatio: window.devicePixelRatio || 1.0,
    orientation: screen.orientation?.type || 'unknown',
  };

  // Test Canvas 2D
  let canvas2d = false;
  try {
    const c = document.createElement('canvas');
    canvas2d = !!c.getContext('2d');
  } catch {
    canvas2d = false;
  }

  // Test WebGL / WebGL2
  let webgl = false;
  let webgl2 = false;
  try {
    const c = document.createElement('canvas');
    webgl = !!c.getContext('webgl');
    webgl2 = !!c.getContext('webgl2');
  } catch {
    webgl = false;
    webgl2 = false;
  }

  // Test WebAssembly & SIMD
  let webAssembly = typeof WebAssembly === 'object';
  let wasmSimd = false;
  if (webAssembly) {
    try {
      wasmSimd = WebAssembly.validate(
        new Uint8Array([0, 97, 115, 109, 1, 0, 0, 0, 1, 5, 1, 96, 0, 1, 123, 3, 2, 1, 0, 10, 10, 1, 8, 0, 253, 15, 253, 98, 11])
      );
    } catch {
      wasmSimd = false;
    }
  }

  // Test Web Workers
  const webWorker = typeof Worker !== 'undefined';

  // Test WebCodecs & H.264 profiles
  let webCodecs = false;
  let h264Baseline = false;
  let h264Main = false;
  let h264High = false;

  // Type definition for VideoDecoder if not in standard TS lib
  const win = window as unknown as { VideoDecoder?: { isConfigSupported: (config: { codec: string }) => Promise<{ supported: boolean }> } };

  if (typeof win.VideoDecoder !== 'undefined') {
    webCodecs = true;
    try {
      const baseline = await win.VideoDecoder.isConfigSupported({ codec: 'avc1.42E01F' });
      h264Baseline = !!baseline.supported;
    } catch {
      h264Baseline = false;
    }

    try {
      const main = await win.VideoDecoder.isConfigSupported({ codec: 'avc1.4D401F' });
      h264Main = !!main.supported;
    } catch {
      h264Main = false;
    }

    try {
      const high = await win.VideoDecoder.isConfigSupported({ codec: 'avc1.64001F' });
      h264High = !!high.supported;
    } catch {
      h264High = false;
    }
  }

  // Test MediaSource
  const mediaSource = typeof window.MediaSource !== 'undefined';

  // Test WebRTC
  const webRtc = typeof window.RTCPeerConnection !== 'undefined';

  // Test AudioContext
  const audioContext = typeof window.AudioContext !== 'undefined' || typeof (window as unknown as { webkitAudioContext: unknown }).webkitAudioContext !== 'undefined';

  // Test Touch & Pointer
  const touchEvents = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  const pointerEvents = typeof window.PointerEvent !== 'undefined';

  // Test Private Network Access probe (non-blocking fetch test)
  let privateNetworkAccess = false;
  try {
    // Probe localhost/loopback port check or PNA attribute existence
    privateNetworkAccess = 'targetAddressSpace' in Request.prototype;
  } catch {
    privateNetworkAccess = false;
  }

  // Renderer selection decision logic:
  // Tier 1: WebCodecs if H.264 baseline/main supported
  // Tier 2: WASM H.264 if WebAssembly supported
  // Tier 3: Canvas 2D fallback
  let selectedRenderer: 'webcodecs' | 'wasm-h264' | 'canvas-fallback' = 'canvas-fallback';
  if (webCodecs && (h264Baseline || h264Main)) {
    selectedRenderer = 'webcodecs';
  } else if (webAssembly) {
    selectedRenderer = 'wasm-h264';
  }

  return {
    timestamp: new Date().toISOString(),
    userAgent,
    viewport,
    features: {
      webCodecs,
      h264Baseline,
      h264Main,
      h264High,
      webAssembly,
      wasmSimd,
      webWorker,
      canvas2d,
      webgl,
      webgl2,
      mediaSource,
      webRtc,
      audioContext,
      touchEvents,
      pointerEvents,
      privateNetworkAccess,
    },
    selectedRenderer,
  };
}
